import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createRaffleSchema } from '../../utils/validation';
import { useCreateRaffle } from '../../hooks/useRaffles';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Input } from '../../components/common/Input';
import { Textarea } from '../../components/common/Textarea';
import { Select } from '../../components/common/Select';
import { Button } from '../../components/common/Button';
import { DRAW_SCHEDULES, PARTICIPATION_ACCESS, COUNTRIES } from '../../utils/constants';
import { localDateTimeToUTC } from '../../utils/formatters';
import { CreateRaffleData } from '../../types';

export const AdminCreateRaffle: React.FC = () => {
  const navigate = useNavigate();
  const createRaffle = useCreateRaffle();

  const { register, handleSubmit, formState: { errors } } = useForm<CreateRaffleData>({
    resolver: zodResolver(createRaffleSchema),
  });

  const onSubmit = async (data: CreateRaffleData) => {
    const raffleData = {
      ...data,
      salesStartDate: localDateTimeToUTC(data.salesStartDate),
      salesEndDate: localDateTimeToUTC(data.salesEndDate),
    };
    await createRaffle.mutateAsync(raffleData);
    navigate('/admin/raffles');
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Create New Raffle</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">Set up a new raffle for the platform</p>
      </div>
      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Input label="Raffle Name" {...register('name')} error={errors.name?.message} placeholder="Enter raffle name (10-250 characters)" required />
          <Textarea label="Description" {...register('description')} error={errors.description?.message} placeholder="Describe your raffle (50-5000 characters)" rows={5} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Ticket Price" type="number" step="0.01" {...register('ticketPrice', { valueAsNumber: true })} error={errors.ticketPrice?.message} required />
            <Select label="Currency" {...register('currency')} error={errors.currency?.message} options={[{ value: '', label: 'Select currency' }, ...COUNTRIES.map((c) => ({ value: c.currency, label: `${c.currency} (${c.currencySymbol})` }))]} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Sales Start Date & Time (Local)" type="datetime-local" {...register('salesStartDate')} error={errors.salesStartDate?.message} helperText="For recurring draws, this time will be used for all draws" required />
            <Input label="Sales End Date & Time (Local)" type="datetime-local" {...register('salesEndDate')} error={errors.salesEndDate?.message} helperText="For ONE_TIME draws, the draw occurs at this time" required />
          </div>
          <Select label="Draw Schedule" {...register('drawSchedule')} error={errors.drawSchedule?.message} options={[{ value: '', label: 'Select schedule' }, ...DRAW_SCHEDULES]} helperText="ONE_TIME: Draw at sales end. Recurring: Draw at sales start time daily/weekly/monthly/yearly" required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Number of Winners" type="number" min="1" {...register('numberOfWinners', { valueAsNumber: true })} error={errors.numberOfWinners?.message} required />
            <Input label="Prize Distribution (%)" {...register('prizeDistribution')} error={errors.prizeDistribution?.message} placeholder="e.g., 50,30,20" helperText="Comma-separated percentages for each winner" required />
          </div>
          <Input label="Organizer Share (%)" type="number" step="0.01" min="0" max="100" {...register('organizerShare', { valueAsNumber: true })} error={errors.organizerShare?.message} helperText="Commission from ticket sales" required />
          <Select label="Participation Access" {...register('participationAccess')} error={errors.participationAccess?.message} options={[{ value: '', label: 'Select access type' }, ...PARTICIPATION_ACCESS]} required />
          <Input label="Image URL (Optional)" {...register('imageUrl')} error={errors.imageUrl?.message} placeholder="https://example.com/image.jpg" />
          <div className="flex gap-4">
            <Button type="submit" isLoading={createRaffle.isPending}>Create Raffle</Button>
            <Button type="button" variant="outline" onClick={() => navigate('/admin/raffles')}>Cancel</Button>
          </div>
        </form>
      </Card>
    </AdminLayout>
  );
};